import {HighlightViewProps} from './types'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {styled} from '../../stitches/config'
import useLightbox from 'hooks/lightbox'

const StyledHighlight = styled('div', {
    '& blockquote': {
        borderLeft: '2px solid $highlight',
        display: 'flex',
        margin: 0,
        padding: '4px 4px 4px 10px',
    },
    '& em > strong': {
        backgroundColor: '$highlight',
        borderRadius: '0.2rem',
        color: '#FFFFFF',
        display: 'inline-block',
        fontStyle: 'normal',
        fontWeight: 'normal',
        padding: '1px 4px',
    },
    '& img': {
        cursor: 'pointer',
        height: 'auto',
        maxWidth: '100%',
    },
    '& ol': {
        '& li': {lineHeight: 'normal'},
        'lineHeight': '0.2rem',
        'margin': 0,
        'paddingInlineStart': '16px',
    },
    '& p': {margin: 0},
    '& pre': {
        margin: 0,
        whiteSpace: 'pre-wrap',
    },
    '& ul': {
        '& li': {lineHeight: 'normal'},
        'lineHeight': '0.2rem',
        'margin': 0,
        'paddingInlineStart': '16px',
    },
    'display': 'inline-block',
    'fontSize': '$messageText',
    'variants': {
        blockHighlight: {
            true: {
                border: '1px solid $highlight',
                borderRadius: '0.2rem',
                boxSizing: 'border-box',
                padding: '1px 4px',
            },
        },
    },
    'whiteSpace': 'pre-wrap',
    'wordBreak': 'break-word',
})

const components = {
    // Insert 'noopener' and 'noreferrer' rel to links
    a: (props: React.HTMLProps<HTMLAnchorElement>): React.ReactElement => <a {...props} rel='noopener noreferrer' />,
    em: (props: React.HTMLProps<HTMLElement>): React.ReactElement => <em {...props} />,
}

const HighlightView: React.FC<HighlightViewProps> = (props: HighlightViewProps): React.ReactElement => {
    const {
        highlightString, blockHighlight, linkTarget, isChatMessage,
    } = props

    const {open} = useLightbox()

    // Delegate clicks so markdown-rendered images open in the lightbox
    const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
        const target = event.target as HTMLElement
        if (target instanceof HTMLImageElement && isChatMessage) {
            event.preventDefault()
            open(target.src, null)
        }
    }

    return (
        <StyledHighlight blockHighlight={blockHighlight} onClick={handleClick}>
            <ReactMarkdown
                components={components}
                linkTarget={linkTarget || '_blank'}
                remarkPlugins={[remarkGfm]}
            >
                {highlightString}
            </ReactMarkdown>
        </StyledHighlight>
    )
}

export default React.memo(HighlightView)
